import type { CheckoutItemSource, CheckoutPolicyViolation } from './checkout_source.js';

type ItemOf<P extends CheckoutItemSource['productType']> = Extract<
  CheckoutItemSource,
  { productType: P }
>;

/** Quem paga a reserva: `'every-traveler'`, cada viajante com o próprio rateio;
 *  `'requester'`, só o bloco do solicitante (ou o do viajante da política numa
 *  reserva só para terceiros, `allocationOwnerId`). */
export type CheckoutAllocationOwner = 'every-traveler' | 'requester';

/**
 * As regras de checkout que cada produto declara, sem React: o card do chat e a
 * tela cheia leem as mesmas (`checkoutOfferModel`). O manifesto de campos é a
 * outra metade do contrato (`fieldsFor`).
 *
 * Métodos, não propriedades-função, de propósito: o parâmetro bivariante deixa
 * o contrato de um produto caber no tipo do registro inteiro (`productContract`).
 */
interface CheckoutProductContract<P extends CheckoutItemSource['productType']> {
  /** O preço é por assento e multiplica pelos viajantes. */
  readonly scalesByTraveler: boolean;
  /** O BFF julga o envio pelo mesmo dono, declarado de novo no contrato do fio
   *  (`allocationOwnerFor`, genui-contract); o genui-kit testa que batem. */
  readonly allocationOwner: CheckoutAllocationOwner;
  /** O envio de UM viajante repete o bloco no formato antigo do topo
   *  (`allocations`/`traveler`) — transição da #4509, ver `checkout_submit.ts`. */
  readonly legacySubmitEcho: boolean;
  /** Sem roster no bloco, a tela falha fechada em vez de cair no solicitante:
   *  o voo nunca sai em nome de quem ninguém escolheu. Os demais blocos antigos
   *  do histórico (sem `travelers`) caem no solicitante. */
  readonly requiresRoster: boolean;
  /** Início da viagem/estadia/locação (YYYY-MM-DD); ausente, o saldo cai em hoje. */
  startDate(item: ItemOf<P>): string | undefined;
  /** Diárias/noites do painel de preço; ausente onde o produto não tem. */
  units(item: ItemOf<P>): number | undefined;
  /** Hóspedes por quarto (hotel): com ela, cada titular de quarto tem o seu
   *  rateio (#5522, `hotelRooms`); ausente, só o dono do rateio aloca. */
  guestsPerRoom(item: ItemOf<P>): number | undefined;
  /** As regras que reprovaram a cotação — o porquê da justificativa obrigatória. */
  policyViolations(item: ItemOf<P>): readonly CheckoutPolicyViolation[];
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}/;

/** A data de um carimbo ISO; `undefined` para o que não é data (o voo de
 *  fixture e de cards antigos traz só o horário, «08:00»). */
function isoDate(value: string | undefined): string | undefined {
  return value && ISO_DATE.test(value) ? value.slice(0, 10) : undefined;
}

const none = (): undefined => undefined;
const noViolations = (): readonly CheckoutPolicyViolation[] => [];

const CHECKOUT_PRODUCT_CONTRACTS: {
  readonly [P in CheckoutItemSource['productType']]: CheckoutProductContract<P>;
} = {
  rental: {
    scalesByTraveler: false,
    // O condutor é o solicitante: um rateio por reserva.
    allocationOwner: 'requester',
    legacySubmitEcho: false,
    requiresRoster: false,
    startDate: (item) => isoDate(item.detail.pickupAt),
    units: (item) => item.detail.days,
    guestsPerRoom: none,
    policyViolations: noViolations,
  },
  hotel: {
    // `price` já é o do grupo de quartos: o modal-session multiplica por N
    // quartos antes de publicar (`totalForRooms`) — escalar contaria duas vezes.
    scalesByTraveler: false,
    // Cada titular de quarto paga o seu com o próprio rateio (#5522,
    // `hotelRooms`); sem a ocupação, o dono do rateio paga a reserva.
    allocationOwner: 'requester',
    legacySubmitEcho: false,
    requiresRoster: false,
    startDate: (item) => isoDate(item.detail.checkIn),
    units: (item) => item.detail.nights,
    guestsPerRoom: (item) => item.detail.guestsPerRoom,
    policyViolations: noViolations,
  },
  flight: {
    // A busca de oferta roda com 1 passageiro: o preço é por assento.
    scalesByTraveler: true,
    allocationOwner: 'every-traveler',
    legacySubmitEcho: false,
    requiresRoster: true,
    startDate: (item) => isoDate(item.detail.outbound.departAt),
    units: none,
    guestsPerRoom: none,
    policyViolations: (item) => item.detail.policyViolations ?? [],
  },
  bus: {
    scalesByTraveler: false,
    allocationOwner: 'every-traveler',
    legacySubmitEcho: false,
    requiresRoster: false,
    startDate: (item) => isoDate(item.detail.departure.dateTime),
    units: none,
    guestsPerRoom: none,
    policyViolations: noViolations,
  },
  mobility: {
    scalesByTraveler: false,
    // Um viajante só (que pode ser um terceiro): o rateio é o dele.
    allocationOwner: 'every-traveler',
    legacySubmitEcho: true,
    requiresRoster: false,
    startDate: (item) => isoDate(item.detail.outward.scheduleDate),
    units: none,
    guestsPerRoom: none,
    policyViolations: noViolations,
  },
};

/** O contrato do produto do item. O cast é o limite conhecido do TS para uniões
 *  correlacionadas (microsoft/TypeScript#30581): indexar o registro por
 *  `item.productType` não liga o contrato ao item, mas o tipo mapeado garante
 *  que `CHECKOUT_PRODUCT_CONTRACTS[p]` é o contrato de `p`. */
export function productContract<I extends CheckoutItemSource>(
  item: I,
): CheckoutProductContract<I['productType']> {
  return CHECKOUT_PRODUCT_CONTRACTS[item.productType] as CheckoutProductContract<I['productType']>;
}

/** The rules that rejected the quote, over every item of the cart (a
 *  multi-leg trip joins its legs' reasons). */
export function policyViolationsOf(
  items: readonly CheckoutItemSource[],
): readonly CheckoutPolicyViolation[] {
  return items.flatMap((item) => productContract(item).policyViolations(item));
}
